import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
    inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { of } from 'rxjs';
import {
    finalize,
    first,
    map,
    shareReplay,
    switchMap,
    tap
} from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import {
    applyFlyoutUpdate,
    buildApplicationPageValueModel,
    buildDynamicForm,
    buildFormValuesFromAnswers,
    initFlyoutObserver,
    matchesConditions,
    normalizeValidatorConfigs,
    positionFlyoutTooltip
} from '../../../../core/helpers';
import {
    ClientService,
    DynamicFormOptionsService
} from '../../../../core/services';
import { DynamicFormApiService } from '../../../../core/services/dynamic-form-api.service';
import { ScrollTopService } from '../../../../core/services/scroll-top.service';
import { Client } from '../../../../domain/models';
import {
    ClientPageSectionModel,
    PageSectionColumnModel,
    PageSectionFieldValidator,
    PageSectionRowModel
} from '../../../../domain/models/dynamic-form-schema.model';
import { FormPage } from '../../../../domain/models/form-page.enum';
import { ChangesNotSavedComponent } from '../../../../shared/components/changes-not-saved';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';
import { SectionFlyoutComponent } from '../../components/section-flyout/section-flyout.component';
import { Application } from '../../models';
import { applicationPresent } from '../../operators';
import { ApplicationService, CurrentApplicationService } from '../../services';

@Component({
    selector: 'app-disclosure',
    templateUrl: './disclosure.component.html',
    styleUrls: [
        './disclosure.component.scss',
        './../../application-page.component.scss'
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        TranslocoModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        DynamicFormComponent,
        FlexLayoutModule,
        MatButtonModule,
        MatDividerModule,
        SectionFlyoutComponent
    ]
})
export class DisclosureComponent
    extends ChangesNotSavedComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    submitted = false; // because we are extending ChangesNotSavedComponent
    submitting = false;
    activeFlyoutIndex = -1;
    flyoutFlipUp = false;

    client: Client | undefined;

    disclosureForm: UntypedFormGroup;
    private applicationId?: number;
    private loadedSections: ClientPageSectionModel[] = [];
    private flyoutObserver?: IntersectionObserver;

    @ViewChildren('sectionWrapper') sectionWrappers!: QueryList<ElementRef>;

    formSections$ = this.dynamicFormApi
        .getFormSections(FormPage.disclosure)
        .pipe(
            map((sections) =>
                this.dynamicFormOptionsService.enrichSectionsWithOptions(
                    sections
                )
            ),
            shareReplay(1)
        );

    formValue$ = this._currentApplication.latest.pipe(
        applicationPresent(),
        first(),
        switchMap((application: Application) => {
            if (application?.applicationId) {
                this.applicationId = application.applicationId;
                return this.applicationService.getDynamicFormValues(
                    application.applicationId!,
                    FormPage.disclosure
                );
            } else return of(null);
        })
    );

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly applicationService: ApplicationService,
        private readonly snackBar: MatSnackBar,
        private readonly router: Router,
        private readonly _transloco: TranslocoService,
        private readonly _currentApplication: CurrentApplicationService,
        private readonly _client: ClientService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService,
        private readonly dynamicFormApi: DynamicFormApiService,
        private readonly dynamicFormOptionsService: DynamicFormOptionsService,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _scrollTop: ScrollTopService
    ) {
        super();
        this.disclosureForm = this._fb.group({});
    }

    ngAfterViewInit() {
        this.flyoutObserver = initFlyoutObserver(
            this.sectionWrappers,
            () => this._updateActiveFlyout(),
            this.destroyRef
        );
    }

    ngOnDestroy() {
        this.flyoutObserver?.disconnect();
    }

    private _updateActiveFlyout(): void {
        applyFlyoutUpdate(
            this.sectionWrappers,
            this._cdr,
            () => this._positionFlyoutTooltip(),
            (state) => {
                this.activeFlyoutIndex = state.activeFlyoutIndex;
                this.flyoutFlipUp = state.flyoutFlipUp;
            }
        );
    }

    private _positionFlyoutTooltip(): void {
        positionFlyoutTooltip(
            this.activeFlyoutIndex,
            this.flyoutFlipUp,
            this.sectionWrappers
        );
    }

    ngOnInit() {
        this._googleAnalyticsService.pageView('Application: Disclosure');

        this._client.latest
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((client) => {
                this.client = client;
            });

        this.formSections$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((sections) => {
                this.loadedSections = sections;
                this.disclosureForm = buildDynamicForm(sections, this._fb);

                this.formValue$.pipe(first()).subscribe((formValue) => {
                    if (!formValue) {
                        return;
                    }

                    const values = buildFormValuesFromAnswers(
                        sections,
                        formValue
                    );

                    const parentSignatureRequired =
                        formValue.parentSignatureRequired === true ||
                        formValue.ParentSignatureRequired === true;
                    if (
                        !this.disclosureForm.contains('ParentSignatureRequired')
                    ) {
                        this.disclosureForm.addControl(
                            'ParentSignatureRequired',
                            this._fb.control(parentSignatureRequired)
                        );
                    } else {
                        this.disclosureForm
                            .get('ParentSignatureRequired')
                            ?.setValue(parentSignatureRequired);
                    }

                    this.disclosureForm.patchValue(values);
                    setTimeout(() => this._scrollTop.scrollToTop(), 0);

                    this.disclosureForm
                        .get('ParentSignatureRequired')
                        ?.setValue(parentSignatureRequired);
                });
            });
    }

    getSectionValidators(
        section: ClientPageSectionModel
    ): PageSectionFieldValidator[] {
        return normalizeValidatorConfigs(section.validators, {
            forceUniqueTargetKeys: true
        });
    }

    isSectionVisible(section: ClientPageSectionModel): boolean {
        return matchesConditions(this.disclosureForm, section.visibleWhen);
    }

    getSectionErrors(
        section: ClientPageSectionModel
    ): PageSectionFieldValidator[] {
        const anyFieldTouched = section.rows
            .reduce(
                (acc: PageSectionColumnModel[], row: PageSectionRowModel) =>
                    acc.concat(row.fields),
                []
            )
            .some(
                (field: PageSectionColumnModel) =>
                    this.disclosureForm.get(field.key)?.touched
            );

        if (!this.submitted && !anyFieldTouched) {
            return [];
        }

        return this.getSectionValidators(section).filter(
            (validator) =>
                !validator.target &&
                this.disclosureForm.hasError(
                    validator.errorKey ?? validator.name
                )
        );
    }

    onSubmitted() {
        if (this.submitting) return;
        this.disclosureForm.markAllAsTouched();
        this.submitted = true;

        if (this.disclosureForm.invalid) {
            this._transloco
                .selectTranslate('application.snack.invalid-form')
                .pipe(first())
                .subscribe((message) =>
                    this.snackBar.open(message, undefined, { duration: 4000 })
                );
            return;
        }

        if (!this.applicationId || this.loadedSections.length === 0) {
            return;
        }

        this.submitting = true;

        const payload = buildApplicationPageValueModel(
            this.loadedSections,
            this.disclosureForm,
            this.applicationId,
            FormPage.disclosure
        );

        this.applicationService
            .saveDynamicFormValues(
                this.applicationId,
                FormPage.disclosure,
                payload
            )
            .pipe(
                switchMap(() =>
                    this._transloco
                        .selectTranslate('application.snack.saved')
                        .pipe(first())
                ),
                tap((message) => {
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    });
                    this.postSubmit();
                }),
                finalize(() => (this.submitting = false))
            )
            .subscribe();
    }

    postSubmit() {
        this.submitted = true;
        this._currentApplication.reset();
        this.router.navigateByUrl(
            `/${runtimeConfig.clientCode}/application-confirmed`
        );
    }

    isChanged(): boolean {
        return this.disclosureForm?.touched ?? false;
    }

    isSaved(): boolean {
        return this.submitted;
    }
}
